import React from 'react';
import { MoreVertical, Pin } from 'lucide-react';
import ConversationDropdown from './ConversationDropdown';

/**
 * ConversationListItemCard - Individual conversation item in the list
 */
const ConversationListItemCard = ({
  conversation,
  isSelected,
  isPinned,
  isDropdownOpen,
  onSelect,
  onToggleDropdown,
  onCloseDropdown,
  onTogglePin,
  onAddToMeeting,
  onNewMeeting,
  onAddContact,
  onDelete,
  formatTimestamp,
  getAccountCategories,
  getCategoryBadge,
  getContactAvatar,
  className = ''
}) => {
  const conv = conversation;
  const categories = getAccountCategories ? getAccountCategories(conv) : [];
  const avatar = getContactAvatar ? getContactAvatar(conv) : (conv.user_avatar || conv.avatar || conv.image);
  
  return (
    <div className="relative">
      <div
        onClick={() => onSelect?.(conv)}
        data-testid={`conversation-${conv.id}`}
        className={`group relative p-2.5 md:p-3.5 rounded-xl cursor-pointer transition-all ${
          isSelected
            ? 'bg-gray-800 border border-gray-400/30'
            : 'hover:bg-gray-800/50 border border-transparent'
        } ${className}`}
      >
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden">
            {avatar ? (
              <img 
                src={avatar} 
                alt={conv.name} 
                className="w-full h-full object-cover rounded-full"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div 
              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold text-sm"
              style={{ display: avatar ? 'none' : 'flex' }}
            >
              {conv.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="text-sm font-semibold text-white truncate">
                  {conv.name}
                  {conv.isGroup && <span className="ml-1 text-xs text-gray-500">(Group)</span>}
                </h4>
                {/* Category Badges */}
                {categories.map((category, idx) => {
                  const badge = getCategoryBadge ? getCategoryBadge(category) : { bg: 'bg-gray-500/20', text: 'text-gray-400', label: category };
                  return (
                    <span key={idx} className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {formatTimestamp ? formatTimestamp(conv.timestamp || conv.time) : conv.time}
                </span>
                
                {/* 3-dot dropdown button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDropdown?.();
                  }}
                  className="p-1 hover:bg-gray-700 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                  data-testid={`conv-menu-${conv.id}`}
                >
                  <MoreVertical size={16} className="text-gray-400" />
                </button>
              </div>
            </div>
            {/* Company · Role byline — only renders when contact has company/role
                metadata (e.g. counterfind mock conversations + future enriched contacts) */}
            {(conv.company || conv.role) && (
              <p
                className="text-[11px] text-gray-500 truncate -mt-0.5 mb-1"
                data-testid={`conv-company-role-${conv.id}`}
              >
                {[conv.company, conv.role].filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400 truncate flex-1">
                {conv.hasVoiceMessage ? (
                  <span className="italic text-purple-400">🎤 Voice message</span>
                ) : (
                  (conv.lastMessage || '').replace(/￼/g, '📎 ')
                )}
              </p>
              {conv.unread > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {conv.unread}
                </span>
              )}
              {isPinned && conv.unread === 0 && (
                <Pin size={14} className="ml-2 text-purple-400 fill-purple-400" />
              )}
            </div>
          </div>
        </div>
        
        {/* Dropdown Menu */}
        <ConversationDropdown
          isOpen={isDropdownOpen}
          isPinned={isPinned}
          onPin={onTogglePin}
          onAddToMeeting={onAddToMeeting}
          onNewMeeting={onNewMeeting}
          onAddContact={onAddContact}
          onDelete={onDelete}
          onClose={onCloseDropdown}
        />
      </div>
    </div>
  );
};

export default ConversationListItemCard;
